import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FaPlusCircle, FaCheckCircle, FaShieldAlt, FaUserFriends, FaEye, FaTooth
} from "react-icons/fa";

const FAQItem = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-5 py-4 font-semibold flex justify-between items-center"
        aria-expanded={open}
        aria-controls={`faq-${i}`}
      >
        <span>{q}</span>
        <span className={`transform transition ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && (
        <div id={`faq-${i}`} className="px-5 pb-5 text-gray-600">
          {a}
        </div>
      )}
    </div>
  );
};

export default function MedicareAdvantage() {
  return (
    <>
      <Helmet>
        <title>Medicare Advantage (Part C) | Your Choice Associates</title>
        <meta
          name="description"
          content="Compare Medicare Advantage (Part C) and Medicare Supplement (Medigap). Understand networks, out-of-pocket limits, and what each covers so you can choose confidently."
        />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#002b5c] via-[#003e86] to-[#145ea8]" />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative max-w-7xl mx-auto px-6 md:px-8 pt-20 pb-16 text-white"
        >
          <span className="inline-block text-xs font-bold tracking-widest uppercase bg-white/10 backdrop-blur px-3 py-1 rounded-full">
            Medicare Advantage (Part C)
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold mt-4 leading-tight">
            All-in-one coverage with extra benefits
          </h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            Offered by private insurers approved by Medicare. Bundle hospital, medical, and often prescriptions —
            many plans add dental, vision, hearing, fitness, and even transportation.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {[
              { label: "Dental & Vision options" },
              { label: "Max-out-of-pocket protection" },
              { label: "Local provider networks (HMO/PPO)" },
            ].map((b, i) => (
              <span key={i} className="bg-white text-[#002b5c] px-3 py-1 rounded-full text-sm font-semibold">
                {b.label}
              </span>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Key benefits */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <h2 className="text-2xl md:text-3xl font-bold mb-8">Why people choose Part C</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: <FaPlusCircle />, title: "Bundled simplicity", desc: "Hospital, medical, and often prescriptions in one card." },
            { icon: <FaShieldAlt />, title: "Spending protection", desc: "Guaranteed annual max-out-of-pocket (MOOP)." },
            { icon: <FaUserFriends />, title: "Extra perks", desc: "Many plans add dental, vision, hearing, OTC, fitness, and transportation." },
            { icon: <FaEye />, title: "Care coordination", desc: "HMO requires staying in-network; PPO offers more flexibility." },
            { icon: <FaTooth />, title: "Dental & vision", desc: "Popular benefit add-ons not in Original Medicare." },
            { icon: <FaCheckCircle />, title: "Low premiums", desc: "Some areas offer $0 premium options (area-dependent)." },
          ].map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
            >
              <div className="text-2xl">{c.icon}</div>
              <h3 className="mt-4 font-bold text-lg">{c.title}</h3>
              <p className="mt-2 text-gray-600">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it compares */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">How Part C differs from Medigap</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Medicare Advantage (Part C) */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-lg">Medicare Advantage (Part C)</h3>
              <ul className="mt-3 space-y-2 text-gray-600 list-disc pl-5">
                <li>Offered by private insurers approved by Medicare.</li>
                <li>HMO: required to stay in-network. PPO: more flexibility.</li>
                <li>Guaranteed annual max out-of-pocket (MOOP).</li>
                <li>Often includes extra benefits like dental, vision, hearing, fitness, and even transportation.</li>
              </ul>
            </div>

            {/* Medicare Supplement (Medigap) */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-lg">Medicare Supplement (Medigap)</h3>
              <ul className="mt-3 space-y-2 text-gray-600 list-disc pl-5">
                <li>Works with Original Medicare to cover gaps.</li>
                <li>See any provider who accepts Original Medicare.</li>
                <li>Does not include prescription coverage — you’ll need an additional Part D plan.</li>
                <li>Helps pay for deductibles, coinsurance, and copayments.</li>
                <li>Works nationwide with any provider that accepts Medicare.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-16">
        <h2 className="text-2xl md:text-3xl font-bold mb-6">Common questions</h2>
        <div className="space-y-3">
          {[
            {
              q: "When can I enroll in Part C?",
              a: "Typically during your Initial Enrollment Period, the Annual Enrollment Period (Oct 15–Dec 7), or certain Special Enrollment Periods."
            },
            {
              q: "Do all plans include prescriptions?",
              a: "Most do, but not all. We’ll check if a MAPD (Advantage + Part D) or MA-only plan fits your situation."
            },
            {
              q: "Are $0 premium plans real?",
              a: "Yes in many counties. Costs are shared differently—copays and MOOP still apply."
            },
          ].map((f, i) => <FAQItem key={i} i={i} {...f} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#002b5c] text-white rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-lg">
            <div>
              <h3 className="text-2xl md:text-3xl font-extrabold">Compare plans in your ZIP</h3>
              <p className="mt-2 text-white/90">Free, no obligation. A licensed broker will walk you through options.</p>
            </div>
            <a href="/start" className="inline-flex items-center font-semibold bg-white text-[#002b5c] px-5 py-3 rounded-xl hover:opacity-90 transition">
              Talk to a Broker
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
